import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import { PROPOSAL_BUNDLE_CATEGORY, type ProposalBundle } from '../../types/proposalBundles';
import { buildFlightTimeRangeDisplay } from '../proposal/utils';
import { formatRouteDate } from '../../utils/routeInfoFormatter';
import { ForeignFlightTimeRangeCell } from '../proposal/ForeignFlightTimeRangeCell';
import StateLabel from './StateLabel';

interface Props {
  bundle: ProposalBundle;
  includeTax: boolean;
  legLabel: string;
}

const LegBlock = ({ bundle, includeTax, legLabel }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const dateStr = formatRouteDate(bundle.departureDate);
  const routeInfo = bundle.origin && bundle.destination ? `${bundle.origin} → ${bundle.destination}` : null;
  const selectedItem = bundle.items.find(item => item.selected) ?? null;
  const selectedItemDisplayPrice =
    selectedItem != null
      ? (() => {
          const baseFromApi = selectedItem.priceText?.trim() ?? '';
          const base =
            baseFromApi || formatPriceWithMark(includeTax ? selectedItem.priceWithTax : selectedItem.price);
          if (
            base != null &&
            bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD &&
            selectedItem.isAir === true
          ) {
            return baseFromApi.length > 0 ? base : `${base}（往復）`;
          }
          return base ?? '-';
        })()
      : null;

  return (
    <div>
      <div style={styles.bundleHeader}>
        <span style={styles.categoryLabel}>{legLabel}</span>
        <StateLabel state={bundle.state} style={styles.stateLabel} />
      </div>
      {(routeInfo || dateStr) && (
        <div style={styles.routeInfo}>
          {routeInfo && <span>{routeInfo}</span>}
          {dateStr && <span style={styles.dateText}>（{dateStr}）</span>}
        </div>
      )}
      {selectedItem ? (
        <div style={styles.selectedItemInfo}>
          <div style={styles.selectedItemTitle}>
            {selectedItem.flightName || selectedItem.title || '-'}
            {selectedItem.seatClass && <span style={styles.seatClass}>（{selectedItem.seatClass}）</span>}
          </div>
          {selectedItem.departureTime != null && selectedItem.arrivalTime != null && (
            <div style={styles.selectedItemTime}>
              <ForeignFlightTimeRangeCell
                {...buildFlightTimeRangeDisplay(
                  selectedItem.departureTime,
                  selectedItem.arrivalTime,
                  selectedItem.departureDate,
                  selectedItem.arrivalDate
                )}
                fallbackLabel={`${selectedItem.departureTime} → ${selectedItem.arrivalTime}`}
              />
            </div>
          )}
          <div style={styles.selectedItemPrice}>{selectedItemDisplayPrice}</div>
        </div>
      ) : null}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  bundleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px'
  },
  categoryLabel: {
    fontSize: '12px',
    fontWeight: 600 as const,
    flex: 1,
    color: theme.textColor
  },
  stateLabel: {
    fontSize: '11px',
    fontWeight: 500 as const
  },
  routeInfo: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginBottom: '4px'
  },
  dateText: {
    color: theme.grayTextColor
  },
  selectedItemInfo: {
    marginTop: '6px',
    padding: '8px',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px'
  },
  selectedItemTitle: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  seatClass: {
    fontSize: '12px',
    fontWeight: 400 as const
  },
  selectedItemTime: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginTop: '2px'
  },
  selectedItemPrice: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginTop: '4px'
  }
});

export default LegBlock;
